/**
 * The one place a query key is spelled (`rules/tanstack-query.mdc` §2).
 *
 * A key is a cache address, and `invalidateQueries` matches it by prefix. A hook that writes its own
 * `['task', id]` beside a factory that writes `['tasks', 'detail', id]` therefore keeps serving stale
 * data after every mutation — and nothing reports it. Not a type error, not a warning, not a failing
 * test: a screen that is quietly wrong until the user reloads. Centralising the keys is what turns
 * that class of defect into a compile error, and `test/architecture/data-layer-conventions.test.ts`
 * fails the build on a literal array reaching `queryKey`.
 *
 * The hierarchy is fixed: `all` is the prefix every derived key starts with, so one invalidation of
 * a group reaches its lists and its details together.
 */
export interface EntityQueryKeys<TListParams> {
  /** Prefix of the whole group — what a mutation invalidates. */
  readonly all: readonly [string];
  /** One cache entry per distinct filter: the parameters are part of the address. */
  readonly list: (params: TListParams) => readonly [string, 'list', TListParams];
  readonly detail: (id: string) => readonly [string, 'detail', string];
}

export const entityQueryKeys = <TListParams>(scope: string): EntityQueryKeys<TListParams> => ({
  all: [scope],
  list: (params) => [scope, 'list', params],
  detail: (id) => [scope, 'detail', id],
});

/**
 * Активные сессии вызывающего — **один адрес, и второго контракт не публикует**.
 *
 * До этой истории группа была объявлена как `entityQueryKeys<SessionListParams>('sessions')` — с
 * постраничным `list(params)` и с `detail(id)` — и не адресовала ничего: операций за ней не было,
 * читал её только тест формы фабрики. Первый настоящий потребитель показал, что обе записи
 * описывали запросы, которых нельзя сделать: `GET /auth/sessions` **не постранично** («deliberately
 * not paginated» в самой операции — строк столько, сколько устройств, и предел им ставит
 * тридцатидневный срок жизни), а `GET /auth/sessions/{sessionId}` не существует вовсе — по одному
 * идентификатору сессии есть только `DELETE`.
 *
 * Оставленный постраничный ключ обошёлся бы не лишним типом: `list({})` завёл бы адрес с пустым
 * объектом-параметром, а `detail(id)` — адрес, по которому `invalidateQueries` промахивается,
 * выглядя работающим. Тот же довод, по которому у провайдеров моделей нет `detail`.
 *
 * Корень общий, поэтому одна инвалидация по `all` достаёт перечень после любого из трёх действий,
 * которые его меняют, — завершения одной сессии, завершения остальных и **смены пароля**: сервер
 * закрывает все прочие сессии в той же транзакции, что и запись нового пароля.
 */
interface SessionQueryKeys {
  readonly all: readonly [string];
  readonly list: () => readonly [string, 'list'];
}

/**
 * Реестр групп. Группа заводится здесь тем эпиком, который приносит стоящие за ней операции.
 *
 * Прежняя редакция этой фразы называла «сегодняшней» единственной группой сессию клиента и юнит
 * `units/session`, которого нет с EPIC-006: он был поглощён `units/auth`, а групп с тех пор стало
 * два десятка. Снято вместе с приведением группы `Sessions` к контракту.
 */
/**
 * The caller's own permissions — one address, because the question takes no parameters.
 *
 * Not `entityQueryKeys`: that shape carries a list and a detail, and this group has neither. There
 * is exactly one answer per signed-in person, and asking about somebody else is a different
 * operation under a different permission (`GET /me/permissions` takes no subject on purpose).
 */
export interface PermissionQueryKeys {
  readonly all: readonly [string];
  readonly mine: () => readonly [string, 'mine'];
  /**
   * What **one other person** may do, with the layer that decided each key
   * (`GET /users/{userId}/permissions`, `permission-model.md` §7(ж)).
   *
   * A sibling of `mine()` under the same `all` prefix rather than a group of its own, and that is
   * the whole reason it is here: writing an exception changes the subject's `permissionsVersion`,
   * and the subject may be the caller — denying oneself `invoice:issue` is a supported shape
   * (`permission-override.policy.ts`, `governsRights` is deliberately narrow). One
   * `invalidateQueries({ queryKey: QueryKeys.Permissions.all })` therefore has to reach both this
   * address **and** `mine()`, which is what the guards and every `can(...)` in the shell read. Two
   * separate prefixes would leave the caller's own sidebar showing an action they had just taken
   * away from themselves, until a reload.
   */
  readonly ofUser: (userId: string) => readonly [string, 'user', string];
}

/**
 * Roles of the organization — the administration matrix reads one address and writes it back.
 *
 * `matrix()` rather than `list(params)`: the screen asks for every role with everything it grants,
 * because it renders them all against every permission at once. There is no filter that would make
 * a second cache entry meaningful, and the filtering the screen does do — search, groups, «only
 * differences» — happens over an answer it already has.
 */
export interface RoleQueryKeys {
  readonly all: readonly [string];
  readonly matrix: () => readonly [string, 'matrix'];
}

/**
 * Personnel records. `entityQueryKeys`, because this group genuinely has both shapes: one record per
 * person (`detail`) and the directory that lists them (`list`, EPIC-016). Both start with the
 * same prefix, so saving a profile can invalidate the directory with one call.
 */
export interface EmployeeListParams {
  readonly q: string;
  readonly status: readonly string[];
  readonly role: readonly string[];
  readonly team: readonly string[];
  readonly sort: string;
  readonly page: number;
  readonly perPage: number;
}

/**
 * The chart is a third address, not a `list` with a filter.
 *
 * It answers a different question — every edge of the organization, unpaged — behind a different
 * permission, and it does not change when the directory's filters do. Giving it a `list` key would
 * mean a cache entry per filter of a screen whose answer does not depend on any of them.
 */
export interface EmployeeQueryKeys extends EntityQueryKeys<EmployeeListParams> {
  readonly orgChart: () => readonly [string, 'org-chart'];
}

/**
 * Teams — a `list()` that takes no parameters, on purpose.
 *
 * `GET /teams` accepts none: it publishes every live team of the organization in one document, and
 * the screen's phrase, order and page are applied to that answer. Giving the key the filter would
 * keep one cache entry per phrase somebody typed, each of them a copy of the same bytes — and the
 * `detail` of a team would then be invalidated from a list address that depends on what was in the
 * search box at the time.
 *
 * `detail` is here because the roster genuinely is a second read (`GET /teams/{teamId}`), and both
 * start with the same prefix so that adding a member invalidates the roster and the counts on the
 * list together.
 */
export interface TeamQueryKeys {
  readonly all: readonly [string];
  readonly list: () => readonly [string, 'list'];
  readonly detail: (id: string) => readonly [string, 'detail', string];
}

/**
 * Печатные формы — `list` несёт `inCirculation`, единственный параметр `GET /templates` реально
 * принимает; `placeholders` — третий адрес, каталог мест подстановки, общий для всех форм.
 */
export interface TemplateQueryKeys {
  readonly all: readonly [string];
  readonly list: (params: {
    readonly inCirculation: boolean | undefined;
  }) => readonly [string, 'list', { readonly inCirculation: boolean | undefined }];
  readonly detail: (id: string) => readonly [string, 'detail', string];
  readonly placeholders: () => readonly [string, 'placeholders'];
}

/** Сборки документов из форм — `detail` и только: перечня сборок в продукте нет. */
export interface TemplateAssemblyQueryKeys {
  readonly all: readonly [string];
  readonly detail: (id: string) => readonly [string, 'detail', string];
}

/**
 * The recovery-code counter — one address, and there will never be a second.
 *
 * `GET /auth/2fa/recovery-codes` answers `{ total, remaining }` about the caller and takes no
 * parameters, so there is no list and no detail: a `detail(id)` here would suggest an operation
 * that reads somebody else's codes, and no such operation exists or could.
 *
 * **What is deliberately not here is the codes themselves and the drafted secret.** Both are
 * answers to mutations and neither may become a cache entry (CLAUDE.md, «Чувствительность
 * данных»); giving them a key would be the first step towards persisting them.
 */
export interface RecoveryCodeQueryKeys {
  readonly all: readonly [string];
  readonly status: () => readonly [string, 'status'];
}

/**
 * Open invitations — a `list()` with no parameters, because the operation has none.
 *
 * `GET /invitations` accepts no filter, no page and no order: it answers with every unaccepted
 * invitation of the organization, newest first, and the order is the server's. A key carrying
 * anything would therefore be a second cache entry holding the same bytes.
 *
 * No `detail` either, and that is the contract rather than an omission: there is no
 * `GET /invitations/{id}`. The two operations that address one invitation — re-issue and revoke —
 * are writes, and both invalidate the group root, which is why `list()` starts with it.
 */
export interface InvitationQueryKeys {
  readonly all: readonly [string];
  readonly list: () => readonly [string, 'list'];
}

/**
 * The model providers of the organization, and the two answers that hang off one of them.
 *
 * `models(providerId)` is a separate address rather than part of `detail`, because it is a different
 * question with a different lifetime: the catalogue is fetched **from the provider** and may fall
 * back to a configured list (STORY-005-04), so it goes stale on its own schedule and is invalidated
 * by a connection check rather than by an edit. It still starts with the group prefix, so saving a
 * provider reaches the list, the card and the model catalogue in one call — which matters here,
 * because changing the base URL changes which models exist.
 */
/**
 * The provider surface has **no detail address**, and that is the contract rather than an omission.
 *
 * `docs/api/openapi.yaml` publishes no `GET /ai/providers/{providerId}`: the list operation reads the
 * whole tenant in one query — an installation has a handful of providers — and every write answers
 * with the row it produced. A `detail(id)` key would therefore address a request that cannot be
 * made, and cache a second copy of bytes the list already holds; `invalidateQueries` aimed at it
 * would match nothing while looking exactly like an invalidation that works. It was declared here
 * and used by nothing, which is how it stayed unnoticed.
 *
 * `models(providerId)` is the one per-provider entry, and it exists because the operation does
 * (`GET /ai/providers/{providerId}/models`) and because its answer is not part of the row.
 */
export interface AiProviderQueryKeys {
  readonly all: readonly [string];
  readonly list: () => readonly [string, 'list'];
  readonly models: (providerId: string) => readonly [string, 'models', string];
  /**
   * Справочник моделей **вида** вендора, а не строки провайдера.
   *
   * Ключом идёт вид, потому что вопрос про вид: номенклатура вендора одна на всех, и два
   * администратора, добавляющие агрегатор, спрашивают одно и то же. Держать его под
   * `models(providerId)` было бы неверно вдвойне — у создаваемого провайдера нет идентификатора, а
   * общий с ним ключ означал бы, что сохранение провайдера сбрасывает справочник вендора, который от
   * этого сохранения не изменился.
   *
   * Префикс группы тот же: справочник всё-таки про провайдеров, и общая инвалидация группы его
   * задевает — это дешевле, чем второй корень в кеше.
   */
  readonly modelCatalog: (kind: string) => readonly [string, 'model-catalog', string];
}

/**
 * The spend of one period. The period is in the key because it is the question — «сколько ушло в
 * августе» and «сколько ушло в июле» are two answers, and a screen that switched months without
 * switching cache entries would show the previous one until it refetched.
 */
export interface AiUsageQueryKeys {
  readonly all: readonly [string];
  readonly summary: (period: string) => readonly [string, 'summary', string];
}

/**
 * The filter of the journal of calls that left the perimeter — every field of it, because every
 * field changes the answer (`rules/tanstack-query.mdc` §3).
 *
 * `organizationId` is deliberately **not** here: the client never sends it and could not, the server
 * derives it from the session, and putting it in the key would suggest a filter a caller may choose.
 * What protects the cache across a change of organization is `queryClient.clear()` (§13), not the
 * shape of this address.
 */
export interface ExternalCallListParams {
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly provider?: string | undefined;
  readonly outcome?: string | undefined;
  readonly page: number;
  readonly perPage: number;
}

/**
 * Замер качества — три адреса, и ни `list`, ни `detail` среди них нет.
 *
 * `entityQueryKeys` здесь не подходит по той же причине, по которой он снят с сессий: перечня
 * прогонов контракт не публикует вовсе — идентификатор печатает `scripts/run-evaluation.ts`, — так
 * что `list(params)` завёл бы адрес, по которому нельзя сделать запрос, а `invalidateQueries` по
 * нему промахивался бы, выглядя работающим.
 *
 * **Сравнение — свой адрес, а не сводка с параметром.** Оно отвечает на другой вопрос («кто из двух
 * лучше»), и обе стороны входят в ключ: сравнение прогона A с B и его же с C — два разных ответа
 * (`rules/tanstack-query.mdc` §3). Асимметрия сторон сохранена в порядке аргументов, как в самом
 * маршруте: набор заданий читается из происхождения левого прогона.
 *
 * **Проверка адресов набора адресуется набором, а не прогоном.** Набор — конфигурация установки, и
 * два прогона на одном наборе обязаны читать один ответ, а не два одинаковых.
 *
 * Инвалидации у группы нет и не предвидится: прогон неизменяем после записи, а разметка набора —
 * файл установки, который не меняется из интерфейса. Общий корень оставлен затем, чтобы смена
 * организации-арендатора выносила группу целиком (§13).
 */
interface EvaluationQueryKeys {
  readonly all: readonly [string];
  readonly runSummary: (runId: string) => readonly [string, 'run', string];
  readonly runComparison: (
    runId: string,
    against: string,
  ) => readonly [string, 'run', string, 'against', string];
  readonly goldSetAddressCheck: (
    goldSetId: string,
  ) => readonly [string, 'gold-set', string, 'address-check'];
}

/**
 * Отбор реестра документов — **каждое** его поле, потому что каждое меняет ответ
 * (`rules/tanstack-query.mdc` §3), включая дату, на которую спрошено.
 *
 * `organizationId` здесь намеренно нет: клиент его не передаёт и не может, сервер выводит его из
 * сессии, и присутствие в ключе намекало бы на фильтр, который вызывающий волен выбрать. Кеш при
 * смене организации защищает `queryClient.clear()` (§13), а не форма этого адреса.
 */
interface DocumentListParams {
  readonly q: string;
  readonly parse: readonly string[];
  readonly asOf: string | undefined;
  readonly externalProcessing: boolean | undefined;
  readonly sort: string;
  readonly page: number;
  readonly perPage: number;
}

/**
 * Ключи документов: перечень, текст редакции на дату и один пункт.
 *
 * Пункт ключуется всеми тремя частями адреса. Ключ по одному `clauseId` склеил бы один и тот же
 * номер пункта из разных документов — и открытая ссылка показывала бы текст соседнего акта.
 *
 * **`preview` несёт дату, и это не удобство, а инвариант** (`rules/legal-documents.mdc` §15): без
 * неё ответ на вопрос о марте достался бы из кеша, наполненного вопросом о сегодня, — историческое
 * состояние, подменённое актуальным, причём подмена не видна. Страница в ключе по той же причине,
 * что и в любом постраничном чтении: это разные ответы.
 */
interface DocumentQueryKeys {
  readonly all: readonly string[];
  readonly list: (params: DocumentListParams) => readonly unknown[];
  readonly preview: (
    documentId: string,
    asOf: string | undefined,
    page: number,
    clause: string | undefined,
  ) => readonly unknown[];
  /**
   * Оглавление ключуется днём по тому же доводу, что и предпросмотр: ключ без даты отдал бы на
   * вопрос о марте оглавление, положенное в кеш вопросом о сегодня. Страницы у него нет — оно
   * приходит целиком.
   */
  readonly outline: (documentId: string, asOf: string | undefined) => readonly string[];
  readonly clause: (documentId: string, revisionId: string, clauseId: string) => readonly string[];
  /**
   * Без даты в ключе: сверка не читает редакцию на день, она читает **текущее** состояние реквизитов
   * и последний результат — тот же адрес вчера и сегодня, в отличие от `preview`/`outline`.
   */
  readonly editionStatus: (documentId: string) => readonly string[];
}

/**
 * Чем адресуется «сегодня», когда даты в ссылке нет.
 *
 * Сегодняшний день не подставляется здесь числом намеренно: какой день сегодня, решает **сервер** в
 * часовом поясе организации, и клиент, подставивший свой, завёл бы для одного и того же ответа два
 * адреса — по разные стороны полуночи у читателей в разных поясах.
 */
const TODAY = 'today';

/**
 * Проекты документов — **своя группа, а не ветка документов**, и это не вопрос вкуса.
 *
 * Проект не является документом в обращении: на него нельзя сослаться, ответ ассистента его не
 * видит, в реестре его нет (контракт «проекта документа», §1 и §5). Общий с документами префикс
 * означал бы, что приём документа сбрасывает кеш перечня проектов, а выгрузка проекта — кеш
 * реестра: две группы, инвалидирующие друг друга без единой причины.
 */
interface DocumentDraftListParams {
  readonly q: string;
  readonly page: number;
  readonly perPage: number;
}

/**
 * Локальные акты (EPIC-026) — **один адрес, и второго контракт не публикует**.
 *
 * `lifecycle(actId)` соответствует единственной операции чтения, `GET /acts/{actId}/lifecycle`.
 * Ни `list`, ни `detail` здесь нет, и это не упущение шаблона: `docs/api/openapi.yaml` не
 * объявляет ни `GET /acts`, ни чтения акта по документу, поэтому ключ перечня адресовал бы запрос,
 * которого нельзя сделать, — и `invalidateQueries` по нему промахивался бы, выглядя работающим.
 * Тот же довод, по которому у провайдеров моделей нет `detail`.
 *
 * Даты в ключе нет намеренно, в отличие от `Documents.preview`: жизненный цикл — это **текущее**
 * состояние, журнал и связи целиком, а не срез на день. День появится здесь вместе с операцией,
 * которая его принимает.
 *
 * Все записи начинаются с общего корня, поэтому смена статуса и правка связей инвалидируются одним
 * вызовом по `all`: и то и другое меняет и карточку, и строку в перечне — статус акта в реестре тот
 * же самый, и обновить одно, оставив второе, значит показать два ответа на один вопрос.
 */
interface ActListParams {
  readonly q: string;
  readonly status: readonly string[];
  readonly page: number;
  readonly perPage: number;
}

interface ActQueryKeys {
  readonly all: readonly string[];
  readonly list: (params: ActListParams) => readonly unknown[];
  readonly lifecycle: (actId: string) => readonly string[];
}

/**
 * Ознакомления (EPIC-030) — **две поверхности под одним корнем, и обе не `list`**.
 *
 * `ofAct(actId)` — лист одного акта, `mine()` — то, что направлено вызывающему. Разными адресами их
 * делает контракт, а не удобство: право читать лист коллег (`acknowledgement:view_report`) и право
 * видеть своё (`acknowledgement:read`) — разные ключи каталога, и один кеш-адрес с параметром
 * «чей» означал бы, что эти два права различаются одним аргументом.
 *
 * Ни у одной из записей нет параметров: `GET /acts/{actId}/acknowledgements` не принимает ни
 * страницы, ни отбора — он отвечает листом целиком, потому что счётчик `pending` считается по всему
 * листу и постранично не сходился бы; `GET /me/acknowledgements` не принимает ничего вовсе.
 *
 * Общий корень — потому что подтверждение меняет обе стороны: строка, подтверждённая сотрудником,
 * перестаёт быть «не ознакомлен» и в личном перечне, и в отчёте юриста. Одна инвалидация по `all`
 * задевает ровно то, что изменилось, и place вызова про это знать не обязан.
 */
interface AcknowledgementQueryKeys {
  readonly all: readonly string[];
  readonly ofAct: (actId: string) => readonly string[];
  readonly mine: () => readonly string[];
}

/** Разговоры с ассистентом (EPIC-042): перечень и одна лента. */
interface AssistantThreadQueryKeys {
  readonly all: readonly string[];
  readonly list: () => readonly string[];
  readonly thread: (threadId: string) => readonly string[];
}

/**
 * Трасса ответа (STORY-007-06) — своя группа, а не запись под `AssistantThreads`.
 *
 * Адресуется идентификатором **ответа**, а не разговора, и живёт дольше его: одиночный вопрос
 * (`POST /ai/assistant/questions`) разговора не заводит вовсе, а трассу пишет. Положить её под
 * ключ разговора значило бы сделать недостижимой ровно половину трасс — и сделать это молча.
 *
 * Ни `list`, ни `detail`: перечня трасс не существует ни в контракте, ни в замысле. Трасса
 * читается от известного ответа и никогда — перебором, потому что перебор был бы вторым каналом
 * выдачи содержимого документов мимо вопроса, на который оно отвечало.
 *
 * Инвалидации у группы нет и быть не может: строка неизменяема после записи (`app_user` не держит
 * ни `UPDATE`, ни `DELETE` на `answer_traces`). Прочитанная трасса не устаревает — она и есть
 * доказательство того, каким ответ был в момент выдачи.
 */
interface AnswerTraceQueryKeys {
  readonly all: readonly string[];
  readonly ofAnswer: (answerId: string) => readonly string[];
}

export const QueryKeys = {
  Acts: {
    all: ['acts'],
    list: (params: ActListParams) => ['acts', 'list', params],
    lifecycle: (actId: string) => ['acts', 'lifecycle', actId],
  } satisfies ActQueryKeys,
  Acknowledgements: {
    all: ['acknowledgements'],
    ofAct: (actId: string) => ['acknowledgements', 'act', actId],
    mine: () => ['acknowledgements', 'mine'],
  } satisfies AcknowledgementQueryKeys,
  AssistantThreads: {
    all: ['assistant-threads'],
    list: () => ['assistant-threads', 'list'],
    thread: (threadId: string) => ['assistant-threads', 'thread', threadId],
  } satisfies AssistantThreadQueryKeys,
  AnswerTraces: {
    all: ['answer-traces'],
    ofAnswer: (answerId: string) => ['answer-traces', answerId],
  } satisfies AnswerTraceQueryKeys,
  Sessions: {
    all: ['sessions'],
    list: () => ['sessions', 'list'],
  } satisfies SessionQueryKeys,
  AiProviders: {
    all: ['ai-providers'],
    list: () => ['ai-providers', 'list'],
    models: (providerId: string) => ['ai-providers', 'models', providerId],
    modelCatalog: (kind: string) => ['ai-providers', 'model-catalog', kind],
  } satisfies AiProviderQueryKeys,
  DocumentDrafts: entityQueryKeys<DocumentDraftListParams>('document-drafts'),
  Documents: {
    all: ['documents'],
    list: (params: DocumentListParams) => ['documents', 'list', params],
    preview: (
      documentId: string,
      asOf: string | undefined,
      page: number,
      clause: string | undefined,
    ) => [
      'documents',
      'preview',
      documentId,
      asOf ?? TODAY,
      /*
       * Пункт входит в ключ, потому что он **выбирает страницу**: ответ на «страницу 1» и ответ на
       * «страницу, где лежит п. 5.7» — разные ответы, и общий ключ отдал бы один вместо другого.
       */
      clause ?? page,
    ],
    outline: (documentId: string, asOf: string | undefined) => [
      'documents',
      'outline',
      documentId,
      asOf ?? TODAY,
    ],
    clause: (documentId: string, revisionId: string, clauseId: string) => [
      'documents',
      'clause',
      documentId,
      revisionId,
      clauseId,
    ],
    editionStatus: (documentId: string) => ['documents', 'edition-status', documentId],
  } satisfies DocumentQueryKeys,
  AiUsage: {
    all: ['ai-usage'],
    summary: (period: string) => ['ai-usage', 'summary', period],
  } satisfies AiUsageQueryKeys,
  ExternalCalls: entityQueryKeys<ExternalCallListParams>('external-calls'),
  Evaluation: {
    all: ['evaluation'],
    runSummary: (runId: string) => ['evaluation', 'run', runId],
    runComparison: (runId: string, against: string) => [
      'evaluation',
      'run',
      runId,
      'against',
      against,
    ],
    goldSetAddressCheck: (goldSetId: string) => [
      'evaluation',
      'gold-set',
      goldSetId,
      'address-check',
    ],
  } satisfies EvaluationQueryKeys,
  RecoveryCodes: {
    all: ['recovery-codes'],
    status: () => ['recovery-codes', 'status'],
  } satisfies RecoveryCodeQueryKeys,
  Permissions: {
    all: ['permissions'],
    mine: () => ['permissions', 'mine'],
    ofUser: (userId: string) => ['permissions', 'user', userId],
  } satisfies PermissionQueryKeys,
  Roles: {
    all: ['roles'],
    matrix: () => ['roles', 'matrix'],
  } satisfies RoleQueryKeys,
  Employees: {
    ...entityQueryKeys<EmployeeListParams>('employees'),
    orgChart: () => ['employees', 'org-chart'],
  } satisfies EmployeeQueryKeys,
  Invitations: {
    all: ['invitations'],
    list: () => ['invitations', 'list'],
  } satisfies InvitationQueryKeys,
  Teams: {
    all: ['teams'],
    list: () => ['teams', 'list'],
    detail: (id: string) => ['teams', 'detail', id],
  } satisfies TeamQueryKeys,
  /**
   * Печатные формы (EPIC-034). `GET /templates` не постранично — «каталог мал по построению» — и
   * фраза поиска экрана применяется к уже полученному ответу (`narrowTemplates`), поэтому `list`
   * несёт только `inCirculation`: это единственный параметр, который действительно меняет сам
   * запрос. Каталог мест подстановки — третий адрес, а не `detail`: он не про одну форму, а код,
   * общий для всех форм организации (`placeholder-catalog.constant.ts`).
   */
  Templates: {
    all: ['templates'],
    list: (params: { readonly inCirculation: boolean | undefined }) => [
      'templates',
      'list',
      params,
    ],
    detail: (id: string) => ['templates', 'detail', id],
    placeholders: () => ['templates', 'placeholders'],
  } satisfies TemplateQueryKeys,
  /** Сборки документов из форм — отдельная группа: сборка не форма и не документ в обращении. */
  TemplateAssemblies: {
    all: ['template-assemblies'],
    detail: (id: string) => ['template-assemblies', 'detail', id],
  } satisfies TemplateAssemblyQueryKeys,
} as const;
